// Context para gerenciar autenticação
import { createContext, useState, useEffect, ReactNode } from 'react';
import { toast } from 'sonner';
import { apiService } from '@/services/api';
import {
  AUTH_LAST_LOGIN_AT_KEY,
  AUTH_OFFLINE_ALLOWED_UNTIL_KEY,
  AUTH_RELOGIN_MESSAGE_KEY,
  AUTH_USER_CACHE_KEY,
  AUTH_USER_KEY,
  buildOfflineWindow,
  clearActiveAuthStorage,
  clearAllAuthStorage,
  haSessaoMarcada,
  isOfflineWindowValid,
  marcarSessaoAtiva,
  removerTokenLegado,
} from '@/lib/session-utils';
import { photoRepository } from '@/storage/photoRepository';

export interface User {
  id: string;
  email: string;
  nome: string;
  role: string;
  fotoUrl?: string | null;
  assinaturaUrl?: string | null;
  assinaturaDigital?: string | null;
  cargo?: string | null;
  squadId?: string | null;
  squadNome?: string | null;
}

export interface AuthContextType {
  user: User | null;
  /**
   * Há sessão ativa neste navegador. Substitui o antigo `token`: a credencial
   * agora é um cookie HttpOnly e nenhum componente consegue (nem precisa) lê-la.
   */
  sessaoAtiva: boolean;
  loading: boolean;
  login: (email: string, senha: string) => Promise<{ success: boolean; message?: string; user?: User }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isOnline: boolean;
  lastSyncAt?: string;
  offlineSessionActive: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [sessaoAtiva, setSessaoAtiva] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [lastSyncAt, setLastSyncAt] = useState<string | undefined>(undefined);
  const [offlineSessionActive, setOfflineSessionActive] = useState(false);

  const parseUser = (rawUser: string | null): User | null => {
    if (!rawUser) return null;
    try {
      return JSON.parse(rawUser) as User;
    } catch {
      return null;
    }
  };

  const persistOfflineMetadata = () => {
    const { lastLoginAt, offlineAllowedUntil } = buildOfflineWindow();
    localStorage.setItem(AUTH_LAST_LOGIN_AT_KEY, lastLoginAt);
    localStorage.setItem(AUTH_OFFLINE_ALLOWED_UNTIL_KEY, offlineAllowedUntil);
  };

  const clearActiveSessionState = () => {
    clearActiveAuthStorage();
    setSessaoAtiva(false);
    setUser(null);
    setOfflineSessionActive(false);
  };

  const tryActivateOfflineSession = (): boolean => {
    if (navigator.onLine) {
      return false;
    }

    const cachedUser = parseUser(localStorage.getItem(AUTH_USER_CACHE_KEY));
    const offlineAllowedUntil = localStorage.getItem(AUTH_OFFLINE_ALLOWED_UNTIL_KEY);

    if (!cachedUser || !isOfflineWindowValid(offlineAllowedUntil)) {
      return false;
    }

    setSessaoAtiva(false);
    setUser(cachedUser);
    setOfflineSessionActive(true);
    return true;
  };

  // Restaurar a sessão ao iniciar. Quem diz se a credencial ainda vale é o
  // servidor (o cookie é HttpOnly); aqui só se sabe que havia sessão.
  useEffect(() => {
    // Resíduo das versões que guardavam o JWT em localStorage.
    removerTokenLegado();

    const sessaoMarcada = haSessaoMarcada();
    const storedUser = parseUser(localStorage.getItem(AUTH_USER_KEY));
    const cachedUser = parseUser(localStorage.getItem(AUTH_USER_CACHE_KEY));
    const bootUser = storedUser || cachedUser;

    if (sessaoMarcada && bootUser) {
      setSessaoAtiva(true);
      setUser(bootUser);
      setOfflineSessionActive(false);
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(bootUser));
      localStorage.setItem(AUTH_USER_CACHE_KEY, JSON.stringify(bootUser));

      if (!localStorage.getItem(AUTH_OFFLINE_ALLOWED_UNTIL_KEY)) {
        persistOfflineMetadata();
      }
      setLoading(false);
      return;
    }

    if (!tryActivateOfflineSession()) {
      clearActiveSessionState();
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);

      // Sessão somente-offline não deve continuar ativa quando a conectividade volta.
      if (offlineSessionActive && !sessaoAtiva) {
        clearActiveSessionState();
        sessionStorage.setItem(
          AUTH_RELOGIN_MESSAGE_KEY,
          'Conexão restabelecida. Faça login novamente para sincronizar seus dados.'
        );
        return;
      }

      // Verificar fotos pendentes e notificar usuário para sincronizar
      if (sessaoAtiva) {
        photoRepository.getPendingPhotos().then((pending) => {
          if (pending.length > 0) {
            toast.info(
              `Conexão restabelecida. Você tem ${pending.length} foto(s) pendente(s) para sincronizar.`,
              { duration: 8000, id: 'pending-photos-sync' }
            );
          }
        }).catch(() => {
          // Ignora erro ao verificar fotos pendentes
        });
      }
    };

    const handleOffline = () => setIsOnline(false);

    const handleSessionInvalidated = () => {
      clearActiveAuthStorage();
      if (!tryActivateOfflineSession()) {
        setSessaoAtiva(false);
        setUser(null);
        setOfflineSessionActive(false);
      }
      if (navigator.onLine) {
        sessionStorage.setItem(AUTH_RELOGIN_MESSAGE_KEY, 'Sua sessão expirou. Faça login novamente.');
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('auth:session-invalidated', handleSessionInvalidated as EventListener);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('auth:session-invalidated', handleSessionInvalidated as EventListener);
    };
  }, [offlineSessionActive, sessaoAtiva]);

  // Renovação preventiva da sessão.
  //
  // O token de acesso agora dura cerca de uma hora (RG-07). O retry automático
  // de 401 cobre as chamadas que passam pelo apiService.request, mas downloads
  // diretos (PDF, planilhas) e o WebSocket não têm essa rede — renovar em
  // segundo plano evita que caiam por expiração no meio do trabalho.
  useEffect(() => {
    if (!sessaoAtiva) return;

    const renovar = () => {
      if (!navigator.onLine) return;
      apiService.renovarSessao().catch(() => undefined);
    };

    const intervalo = window.setInterval(renovar, 30 * 60 * 1000);
    window.addEventListener('online', renovar);
    return () => {
      window.clearInterval(intervalo);
      window.removeEventListener('online', renovar);
    };
  }, [sessaoAtiva]);

  const login = async (email: string, senha: string) => {
    try {
      const response = await apiService.login(email, senha);

      if (response.success && response.data) {
        setSessaoAtiva(true);
        setUser(response.data.user);
        setOfflineSessionActive(false);

        marcarSessaoAtiva();
        localStorage.setItem(AUTH_USER_KEY, JSON.stringify(response.data.user));
        localStorage.setItem(AUTH_USER_CACHE_KEY, JSON.stringify(response.data.user));
        persistOfflineMetadata();
        setLastSyncAt(new Date().toISOString());
        return { success: true, user: response.data.user };
      } else {
        return {
          success: false,
          message: response.message || 'Erro ao fazer login'
        };
      }
    } catch (error) {
      console.error('Erro ao fazer login:', error);
      return {
        success: false,
        message: 'Erro ao conectar com o servidor'
      };
    }
  };

  const logout = async () => {
    // Avisa o servidor para revogar o refresh token e apagar os cookies —
    // sem isso a sessão continuaria válida no backend depois do "sair".
    await apiService.logoutSessao();
    clearAllAuthStorage();
    setSessaoAtiva(false);
    setUser(null);
    setOfflineSessionActive(false);
    setLastSyncAt(undefined);
  };

  const refreshUser = async () => {
    if (offlineSessionActive) return;

    if (!sessaoAtiva && !haSessaoMarcada()) return;
    if (!navigator.onLine) return;

    try {
      const res = await apiService.getMe();
      if (res.success && res.data) {
        const u = res.data as User;
        setUser(u);
        setSessaoAtiva(true);
        marcarSessaoAtiva();
        localStorage.setItem(AUTH_USER_KEY, JSON.stringify(u));
        localStorage.setItem(AUTH_USER_CACHE_KEY, JSON.stringify(u));
        setLastSyncAt(new Date().toISOString());
        return;
      }

      const unauthorized = (res.message || '').toLowerCase().includes('não autorizado')
        || (res.errors || []).some((e) => String(e).toLowerCase().includes('não autorizado'));
      const isNetworkError = (res.message || '').toLowerCase().includes('conectar')
        || (res.errors || []).some((e) => String(e).toLowerCase().includes('network'));
      if (unauthorized && navigator.onLine) {
        clearActiveSessionState();
        sessionStorage.setItem(AUTH_RELOGIN_MESSAGE_KEY, 'Sua sessão expirou. Faça login novamente.');
      } else if (isNetworkError) {
        // Mantém sessão local quando falha de rede.
        return;
      }
    } catch {
      // ignora erro
    }
  };

  const value: AuthContextType = {
    user,
    sessaoAtiva,
    loading,
    login,
    logout,
    refreshUser,
    isAuthenticated: Boolean(user && (sessaoAtiva || offlineSessionActive)),
    isAdmin: user?.role === 'admin',
    isOnline,
    lastSyncAt,
    offlineSessionActive,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
